// src/data/div40-effective-lives.ts
//
// AUTO-GENERATED from docs/reference/ato/ato-rpg-2025-effective-lives.json
// per audit B130-wave-2c-audit.md §R14.C normalisation rules. The generator
// is a one-shot script (not committed) — re-run when the source JSON updates.
//
// DO NOT EDIT BY HAND — regenerate from the JSON instead.

/**
 * ATO Commissioner's effective lives for Div 40 depreciating assets in
 * residential rental property, normalised from the 12 sub-tables in
 * docs/reference/ato/ato-rpg-2025-effective-lives.json.
 *
 * Source: ATO Rental Properties Guide 2025 (NAT 1729-06.2025) Tables 3–6
 * + sub-tables (bathroom, bedroom, fire_control, kitchen, laundry, outdoor,
 * security_monitoring, machinery, air_conditioning, AC_plant_pre2004,
 * evaporative_coolers).
 *
 * Authority: TR 2024/3 (Commissioner's effective life determinations).
 * Retrieval date: 2025-05-29 (per JSON `current_at` field).
 * Applies to FY: 2024-25 (per JSON `applies_to_fy`).
 *
 * Per audit B130-W2C R14.C, the 12 sub-tables have heterogeneous schemas
 * (3-col / 2-col / 1-col / 2003-vs-2004 boundary / 'own_estimate' sentinel).
 * This file normalises them onto a canonical 3-column shape with explicit
 * nulls and traceability fields. See `tableSourceKey` for back-reference.
 *
 * Capital-works-only rows (Div 43, not Div 40 — RPG flagged) are imported
 * with `capitalWorksOnly: true`. Phase 2 lookupEffectiveLife filters these
 * at lookup time and the picker greys them out with a Div 43 redirect hint.
 */

export interface EffectiveLifeRow {
  /** Stable kebab-case key. Generated from `tableSourceKey` + `asset` to avoid collisions. */
  assetCode: string;
  /** Display name from JSON, verbatim. */
  asset: string;
  /** JSON sub-table key — e.g. "table_3_assets_general". For traceability. */
  tableSourceKey: string;
  /** Human-readable category derived from tableSourceKey (e.g. "Bathroom", "Kitchen"). */
  category: string;
  /** Years; null if column doesn't apply to this sub-table or value is 'own_estimate' or capital-works-only. */
  elPre2004: number | null;
  el2004to2019: number | null;
  elPost2019: number | null;
  /** True if RPG flags this asset as Div 43 capital works only (not Div 40). Picker greys these out. */
  capitalWorksOnly: boolean;
  /** True if any source column was 'own_estimate' string sentinel. Picker falls back to user-typed life with override flag set. */
  isOwnEstimate: boolean;
  /** Verbatim sub-table note from JSON, where present. */
  notes?: string;
}

export const EFFECTIVE_LIVES: readonly EffectiveLifeRow[] = [
  {
    assetCode: "general-cable-trays",
    asset: "Cable trays",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-ceiling-fans",
    asset: "Ceiling fans",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-clocks-electric",
    asset: "Clocks, electric",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 13.333,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-cupboards-other-than-freestanding",
    asset: "Cupboards, other than freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-dvd-players",
    asset: "DVD players",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-door-closers",
    asset: "Door closers",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-door-locks-and-latches-excl-electronic-code-pads",
    asset: "Door locks and latches (excl electronic code pads)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-door-stops-fixed",
    asset: "Door stops, fixed",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-door-stops-freestanding",
    asset: "Door stops, freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-electrical-assets-conduits-distribution-boards-power-points-safety-switches-switchboards-switches-wiring",
    asset: "Electrical assets (conduits, distribution boards, power points, safety switches, switchboards, switches, wiring)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-escalators-machinery-and-moving-parts",
    asset: "Escalators (machinery and moving parts)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-evaporative-coolers",
    asset: "Evaporative coolers",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-facade-fixed",
    asset: "Facade, fixed",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-floor-coverings-fixed-cork-linoleum-parquetry-tiles-vinyl",
    asset: "Floor coverings, fixed (cork, linoleum, parquetry, tiles, vinyl)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-floor-coverings-removable-carpet",
    asset: "Floor coverings, removable: carpet",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-floor-coverings-removable-floating-timber",
    asset: "Floor coverings, removable: floating timber",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-floor-coverings-removable-linoleum",
    asset: "Floor coverings, removable: linoleum",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-floor-coverings-removable-vinyl",
    asset: "Floor coverings, removable: vinyl",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-furniture-freestanding",
    asset: "Furniture, freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 13.333,
    el2004to2019: 13.333,
    elPost2019: 13.333,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-garbage-bins",
    asset: "Garbage bins",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 6.667,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-garbage-chutes",
    asset: "Garbage chutes",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-garbage-compacting-systems-excl-chutes",
    asset: "Garbage compacting systems (excl chutes)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 6.667,
    el2004to2019: 6.667,
    elPost2019: 6.667,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-generators",
    asset: "Generators",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-grease-traps",
    asset: "Grease traps",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-gym-assets-cardiovascular",
    asset: "Gym assets: cardiovascular",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-gym-assets-resistance",
    asset: "Gym assets: resistance",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-hand-dryers-electrical",
    asset: "Hand dryers, electrical",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-hand-rails",
    asset: "Hand rails",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-heaters-fixed-ducts-pipes-vents-wiring",
    asset: "Heaters - fixed: ducts, pipes, vents, wiring",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-heaters-fixed-electric",
    asset: "Heaters - fixed: electric",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-heaters-fixed-fire-places-incl-wood-heaters",
    asset: "Heaters - fixed: fire places (incl wood heaters)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-heaters-gas-ducted-central-heating-unit",
    asset: "Heaters - gas: ducted central heating unit",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-heaters-gas-other",
    asset: "Heaters - gas: other",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-heaters-freestanding",
    asset: "Heaters - freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-home-automation-control-assets",
    asset: "Home automation control assets",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-hooks-robe",
    asset: "Hooks, robe",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-hot-water-systems-excl-piping-electric",
    asset: "Hot-water systems (excl piping): electric",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-hot-water-systems-excl-piping-gas",
    asset: "Hot-water systems (excl piping): gas",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-hot-water-systems-excl-piping-solar",
    asset: "Hot-water systems (excl piping): solar",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-hot-water-system-piping",
    asset: "Hot-water system piping",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-hydronic-assets-controls",
    asset: "Hydronic assets - controls",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-hydronic-assets-water-heaters",
    asset: "Hydronic assets - water heaters",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-insulation",
    asset: "Insulation",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-intercom-system-assets",
    asset: "Intercom system assets",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-lift-wells",
    asset: "Lift wells",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-lifts-hydraulic-and-traction",
    asset: "Lifts (hydraulic and traction)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-lights-fittings-excl-hardwired",
    asset: "Lights: fittings (excl hardwired)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-lights-fittings-hardwired",
    asset: "Lights: fittings, hardwired",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-lights-freestanding",
    asset: "Lights: freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-lights-shades-removable",
    asset: "Lights: shades, removable",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-linen",
    asset: "Linen",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-matv-amplifiers",
    asset: "MATV: amplifiers",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-matv-modulators",
    asset: "MATV: modulators",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-matv-power-sources",
    asset: "MATV: power sources",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-matv-excl-amplifiers-modulators-power-sources",
    asset: "MATV (excl amplifiers, modulators, power sources)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-mirrors-fixed",
    asset: "Mirrors, fixed",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-mirrors-freestanding",
    asset: "Mirrors, freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-radios",
    asset: "Radios",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-ramps",
    asset: "Ramps",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-rugs",
    asset: "Rugs",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 7,
    elPost2019: 7,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-safes-fixed",
    asset: "Safes, fixed",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-sanitary-fixtures-fixed-incl-soap-dispensers",
    asset: "Sanitary fixtures, fixed (incl soap dispensers)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-satellite-dishes",
    asset: "Satellite dishes",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-screens",
    asset: "Screens",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-shelving-other-than-freestanding",
    asset: "Shelving, other than freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-shutters",
    asset: "Shutters",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-signs-fixed",
    asset: "Signs, fixed",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-skylights",
    asset: "Skylights",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-skylights-controls",
    asset: "Skylights - controls",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-skylights-motors",
    asset: "Skylights - motors",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-solar-powered-generating-systems-batteries-inverters-panels-regulators",
    asset: "Solar-powered generating systems (batteries, inverters, panels, regulators)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-stereo-systems-amplifiers-cassette-cd-radios-speakers",
    asset: "Stereo systems (amplifiers, cassette, CD, radios, speakers)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 7,
    elPost2019: 7,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-surround-sound-systems-av-receivers-speakers",
    asset: "Surround sound systems (AV receivers, speakers)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-telecommunications-cordless-phones",
    asset: "Telecommunications: cordless phones",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 4,
    elPost2019: 4,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-telecommunications-distribution-frames",
    asset: "Telecommunications: distribution frames",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-telecommunications-pabx-computerised",
    asset: "Telecommunications: PABX computerised",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 10,
    elPost2019: 7,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-telecommunications-telephone-hand-sets",
    asset: "Telecommunications: telephone hand sets",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 6,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-television-antennas-fixed",
    asset: "Television antennas, fixed",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-television-antennas-freestanding",
    asset: "Television antennas, freestanding",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-television-sets",
    asset: "Television sets",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-vacuum-cleaners-ducted-hoses",
    asset: "Vacuum cleaners - ducted: hoses",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-vacuum-cleaners-ducted-motors",
    asset: "Vacuum cleaners - ducted: motors",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-vacuum-cleaners-ducted-wands",
    asset: "Vacuum cleaners - ducted: wands",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-vacuum-cleaners-portable",
    asset: "Vacuum cleaners - portable",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-vacuum-cleaners-ducted-excl-hoses-motors-wands",
    asset: "Vacuum cleaners, ducted (excl hoses, motors, wands)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-ventilation-ducting-and-vents",
    asset: "Ventilation ducting and vents",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-ventilation-fans",
    asset: "Ventilation fans",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-vcr-systems",
    asset: "VCR systems",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-water-pumps-multi-story",
    asset: "Water pumps - multi-story",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-water-pumps-rainwater-tanks",
    asset: "Water pumps - rainwater tanks",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-water-pumps-single-residence-pressure-pumps",
    asset: "Water pumps - single residence pressure pumps",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-water-tanks",
    asset: "Water tanks",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-window-awnings-insect-screens-louvres-pelmets-tracks",
    asset: "Window awnings, insect screens, louvres, pelmets, tracks",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-window-blinds-internal",
    asset: "Window blinds, internal",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 20,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-window-curtains",
    asset: "Window curtains",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: 6.667,
    el2004to2019: 6,
    elPost2019: 6,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "general-window-shutters-automatic-controls",
    asset: "Window shutters, automatic: controls",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-window-shutters-automatic-motors",
    asset: "Window shutters, automatic: motors",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "general-window-shutters-automatic-excl-controls-and-motors",
    asset: "Window shutters, automatic (excl controls and motors)",
    tableSourceKey: "table_3_assets_general",
    category: "General assets",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "bathroom-accessories-fixed-mirrors-rails-soap-holders-toilet-roll-holders",
    asset: "Accessories, fixed (mirrors, rails, soap holders, toilet roll holders)",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "bathroom-accessories-freestanding-shower-caddies-soap-holders-toilet-brushes",
    asset: "Accessories, freestanding (shower caddies, soap holders, toilet brushes)",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 3,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "bathroom-exhaust-fans-incl-light-heating",
    asset: "Exhaust fans (incl light/heating)",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "bathroom-fixtures-baths-bidets-tapware-toilets-vanity-units-wash-basins",
    asset: "Fixtures (baths, bidets, tapware, toilets, vanity units, wash basins)",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "bathroom-heated-towel-rails-electric",
    asset: "Heated towel rails, electric",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "bathroom-shower-assets-doors-rods-screens-trays",
    asset: "Shower assets (doors, rods, screens, trays)",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "bathroom-shower-curtains-excl-rods-and-screens",
    asset: "Shower curtains (excl rods and screens)",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: 2,
    elPost2019: 2,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "bathroom-spa-baths-excl-pumps",
    asset: "Spa baths (excl pumps)",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "bathroom-spa-bath-pumps",
    asset: "Spa bath pumps",
    tableSourceKey: "table_bathroom",
    category: "Bathroom",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "bedroom-wardrobes-other-than-freestanding-doors-fixed-fittings-mirrors",
    asset: "Wardrobes, other than freestanding (doors, fixed fittings, mirrors)",
    tableSourceKey: "table_bedroom",
    category: "Bedroom",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-alarms-heat",
    asset: "Alarms: heat",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 6,
    elPost2019: 6,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-alarms-smoke",
    asset: "Alarms: smoke",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 6,
    elPost2019: 6,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-detection-alarm-alarm-bells",
    asset: "Detection/alarm: alarm bells",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-detection-alarm-cabling-and-reticulation",
    asset: "Detection/alarm: cabling and reticulation",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-detection-alarm-detectors-addressable-manual-call-points-heat-multi-type-smoke",
    asset: "Detection/alarm: detectors (addressable manual call points, heat, multi-type, smoke)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "fire-control-detection-alarm-fire-indicator-panels",
    asset: "Detection/alarm: fire indicator panels",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-detection-alarm-manual-call-points-non-addressable",
    asset: "Detection/alarm: manual call points (non-addressable)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-doors-fire-and-separation",
    asset: "Doors, fire and separation",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-ewis-master-emergency-control-panels",
    asset: "EWIS: master emergency control panels",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-ewis-speakers",
    asset: "EWIS: speakers",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-ewis-strobe-lights",
    asset: "EWIS: strobe lights",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-ewis-warden-intercom-phone",
    asset: "EWIS: warden intercom phone",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-extinguishers",
    asset: "Extinguishers",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 13.333,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-hose-cabinet-and-reels-excl-hoses-nozzles",
    asset: "Hose cabinet and reels (excl hoses, nozzles)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-hoses-and-nozzles",
    asset: "Hoses and nozzles",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-hydrant-boosters-excl-pumps",
    asset: "Hydrant boosters (excl pumps)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-hydrants",
    asset: "Hydrants",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-lights-exit-and-emergency",
    asset: "Lights, exit and emergency",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-pumps-diesel-and-electric",
    asset: "Pumps (diesel and electric)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: 20,
    el2004to2019: 25,
    elPost2019: 25,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-sprinkler-systems-excl-pumps",
    asset: "Sprinkler systems (excl pumps)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-stair-pressurisation-ac-variable-speed-drives",
    asset: "Stair pressurisation: AC variable speed drives",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "fire-control-stair-pressurisation-pressurisation-extraction-fans",
    asset: "Stair pressurisation: pressurisation/extraction fans",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: 25,
    elPost2019: 25,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "fire-control-stair-pressurisation-sensors",
    asset: "Stair pressurisation: sensors",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "fire-control-water-piping-fire",
    asset: "Water piping (fire)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "fire-control-water-tanks-fire",
    asset: "Water tanks (fire)",
    tableSourceKey: "table_fire_control",
    category: "Fire control",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "kitchen-cook-tops",
    asset: "Cook tops",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "kitchen-crockery",
    asset: "Crockery",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "kitchen-cutlery",
    asset: "Cutlery",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "kitchen-dishwashers",
    asset: "Dishwashers",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "kitchen-fixtures-bench-tops-cupboards-sinks-tapware-tiles",
    asset: "Fixtures (bench tops, cupboards, sinks, tapware, tiles)",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "kitchen-freezers",
    asset: "Freezers",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: 13.333,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "kitchen-garbage-disposal-units",
    asset: "Garbage disposal units",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: 6.667,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "kitchen-microwave-ovens",
    asset: "Microwave ovens",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: 6.667,
    el2004to2019: 10,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "kitchen-ovens",
    asset: "Ovens",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "kitchen-range-hoods",
    asset: "Range hoods",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "kitchen-refrigerators",
    asset: "Refrigerators",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: 13.333,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "kitchen-stoves",
    asset: "Stoves",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: 20,
    el2004to2019: 12,
    elPost2019: 12,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "kitchen-water-filters-electrical",
    asset: "Water filters, electrical",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "kitchen-water-filters-fixed-plumbed",
    asset: "Water filters, fixed (plumbed)",
    tableSourceKey: "table_kitchen",
    category: "Kitchen",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "laundry-clothes-dryers",
    asset: "Clothes dryers",
    tableSourceKey: "table_laundry",
    category: "Laundry",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 7,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "laundry-fixtures-tapware-tiles-tubs",
    asset: "Fixtures (tapware, tiles, tubs)",
    tableSourceKey: "table_laundry",
    category: "Laundry",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "laundry-ironing-boards-freestanding",
    asset: "Ironing boards, freestanding",
    tableSourceKey: "table_laundry",
    category: "Laundry",
    elPre2004: null,
    el2004to2019: 7,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "laundry-ironing-boards-other-than-freestanding",
    asset: "Ironing boards, other than freestanding",
    tableSourceKey: "table_laundry",
    category: "Laundry",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "laundry-irons",
    asset: "Irons",
    tableSourceKey: "table_laundry",
    category: "Laundry",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "laundry-washing-machines",
    asset: "Washing machines",
    tableSourceKey: "table_laundry",
    category: "Laundry",
    elPre2004: 6.667,
    el2004to2019: 10,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-automatic-garage-doors-controls",
    asset: "Automatic garage doors: controls",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-automatic-garage-doors-motors",
    asset: "Automatic garage doors: motors",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-automatic-garage-doors-excl-controls-motors",
    asset: "Automatic garage doors (excl controls, motors)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-barbecues-fixed",
    asset: "Barbecues - fixed",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-barbecues-fixed-sliding-trays-and-cookers",
    asset: "Barbecues - fixed: sliding trays and cookers",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-barbecues-freestanding",
    asset: "Barbecues - freestanding",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-boat-sheds",
    asset: "Boat sheds",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-bollards-fixed",
    asset: "Bollards, fixed",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-car-parks-sealed",
    asset: "Car parks, sealed",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-carports",
    asset: "Carports",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-clotheslines",
    asset: "Clotheslines",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-driveways-sealed",
    asset: "Driveways, sealed",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-fencing",
    asset: "Fencing",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-floor-carpet-artificial-grass-matting",
    asset: "Floor carpet (artificial grass, matting)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-folding-arm-awnings-controls",
    asset: "Folding arm awnings - controls",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-folding-arm-awnings-motors",
    asset: "Folding arm awnings - motors",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-furniture-outdoor-freestanding",
    asset: "Furniture (outdoor), freestanding",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-furniture-outdoor-other-than-freestanding",
    asset: "Furniture (outdoor), other than freestanding",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-garage-doors-excl-motors-controls",
    asset: "Garage doors (excl motors, controls)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-garden-awnings-and-shade-structures-fixed",
    asset: "Garden awnings and shade structures, fixed",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-garden-watering-control-panels",
    asset: "Garden watering: control panels",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-garden-watering-pumps",
    asset: "Garden watering: pumps",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 20,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-garden-watering-timing-devices",
    asset: "Garden watering: timing devices",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-garden-watering-installations-excl-panels-pumps-timers",
    asset: "Garden watering installations (excl panels, pumps, timers)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-garden-lights-fixed",
    asset: "Garden lights, fixed",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-garden-lights-solar",
    asset: "Garden lights, solar",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 8,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-garden-sheds-freestanding",
    asset: "Garden sheds, freestanding",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-garden-sheds-other-than-freestanding",
    asset: "Garden sheds, other than freestanding",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-gates-electrical-controls",
    asset: "Gates, electrical: controls",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-gates-electrical-motors",
    asset: "Gates, electrical: motors",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-gates-excl-electrical-controls-motors",
    asset: "Gates (excl electrical controls, motors)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-jetties-incl-boat-sheds-pontoons",
    asset: "Jetties (incl boat sheds, pontoons)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-letterboxes",
    asset: "Letterboxes",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-operable-pergola-louvres-controls",
    asset: "Operable pergola louvres: controls",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-operable-pergola-louvres-motors",
    asset: "Operable pergola louvres: motors",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-operable-pergola-louvres-excl-controls-motors",
    asset: "Operable pergola louvres (excl controls, motors)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-paths",
    asset: "Paths",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-rainwater-tanks-galvanised-steel",
    asset: "Rainwater tanks - galvanised steel",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 25,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-rainwater-tanks-polyethylene",
    asset: "Rainwater tanks - polyethylene",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-roller-blinds-controls",
    asset: "Roller blinds - controls",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-roller-blinds-motors",
    asset: "Roller blinds - motors",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-retaining-walls",
    asset: "Retaining walls",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-saunas-excl-heating",
    asset: "Saunas (excl heating)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-sauna-heating-assets",
    asset: "Sauna heating assets",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-screens-fixed-incl-glass",
    asset: "Screens, fixed (incl glass)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-septic-tanks",
    asset: "Septic tanks",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-sewage-treatment-controls",
    asset: "Sewage treatment: controls",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 8,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-sewage-treatment-motors",
    asset: "Sewage treatment: motors",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 8,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-sewage-treatment-excl-controls-motors",
    asset: "Sewage treatment (excl controls, motors)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-spas-fixed",
    asset: "Spas - fixed",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-spas-fixed-chlorinators",
    asset: "Spas - fixed: chlorinators",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 12,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-spas-fixed-filtration-incl-pumps",
    asset: "Spas - fixed: filtration (incl pumps)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 12,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-spas-fixed-heaters-electric-gas",
    asset: "Spas - fixed: heaters (electric/gas)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-spas-freestanding-blowers-controls-filters-heaters-pumps",
    asset: "Spas freestanding (blowers, controls, filters, heaters, pumps)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 20,
    el2004to2019: 17,
    elPost2019: 17,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-swimming-pool-chlorinators",
    asset: "Swimming pool: chlorinators",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 12,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-swimming-pool-cleaning",
    asset: "Swimming pool: cleaning",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 7,
    elPost2019: 7,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-swimming-pool-covers-incl-blankets",
    asset: "Swimming pool: covers (incl blankets)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 8,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-swimming-pool-filtration-incl-pumps",
    asset: "Swimming pool: filtration (incl pumps)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 12,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-swimming-pool-electric-heaters",
    asset: "Swimming pool: electric heaters",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-swimming-pool-gas-heaters",
    asset: "Swimming pool: gas heaters",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-swimming-pool-solar-heaters",
    asset: "Swimming pool: solar heaters",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: 13.333,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-swimming-pools-structure",
    asset: "Swimming pools (structure)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "outdoor-tennis-court-cleaners",
    asset: "Tennis court: cleaners",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 3,
    elPost2019: 3,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-tennis-court-drag-brooms",
    asset: "Tennis court: drag brooms",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 3,
    elPost2019: 3,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-tennis-court-nets",
    asset: "Tennis court: nets",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-tennis-court-rollers",
    asset: "Tennis court: rollers",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 3,
    elPost2019: 3,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-tennis-court-umpire-chairs",
    asset: "Tennis court: umpire chairs",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "outdoor-tennis-court-fixed-fences-lights-posts-surfaces",
    asset: "Tennis court: fixed (fences, lights, posts, surfaces)",
    tableSourceKey: "table_outdoor",
    category: "Outdoor",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-access-control-code-pads",
    asset: "Access control: code pads",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "security-access-control-door-controllers",
    asset: "Access control: door controllers",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "security-access-control-proximity-readers",
    asset: "Access control: proximity readers",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: 7,
    elPost2019: 7,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "security-access-control-swipe-card-readers",
    asset: "Access control: swipe card readers",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: 3,
    elPost2019: 3,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "security-cctv-cameras",
    asset: "CCTV: cameras",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: 6.667,
    el2004to2019: 4,
    elPost2019: 4,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-cctv-monitors",
    asset: "CCTV: monitors",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: 6.667,
    el2004to2019: 4,
    elPost2019: 4,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-cctv-digital-recorders",
    asset: "CCTV: digital recorders",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: 4,
    elPost2019: 4,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "security-cctv-time-lapse-recorders",
    asset: "CCTV: time lapse recorders",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: 2,
    elPost2019: 2,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "security-switching-units-incl-multiplexes",
    asset: "Switching units (incl multiplexes)",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "security-doors-and-screens-security",
    asset: "Doors and screens (security)",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-security-code-pads",
    asset: "Security: code pads",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: 6.667,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-security-control-panels",
    asset: "Security: control panels",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: 6.667,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-detectors-glass-pir-vibration",
    asset: "Detectors (glass, PIR, vibration)",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: 6.667,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-detectors-gsm-units-glass-pir-vibration",
    asset: "Detectors - GSM units (glass, PIR, vibration)",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: 6.667,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "security-detector-noise-makers-bells-sirens",
    asset: "Detector noise makers (bells, sirens)",
    tableSourceKey: "table_security_monitoring",
    category: "Security & monitoring",
    elPre2004: 6.667,
    el2004to2019: 5,
    elPost2019: 5,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "machinery-escalators-machinery-and-moving-parts",
    asset: "Escalators (machinery and moving parts)",
    tableSourceKey: "table_4_machinery",
    category: "Machinery (lifts/escalators)",
    elPre2004: 16.667,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
    notes: "2003-vs-2004 boundary inconsistency in source",
  },
  {
    assetCode: "machinery-lifts-electric",
    asset: "Lifts - electric",
    tableSourceKey: "table_4_machinery",
    category: "Machinery (lifts/escalators)",
    elPre2004: 16.667,
    el2004to2019: 30,
    elPost2019: 30,
    capitalWorksOnly: false,
    isOwnEstimate: false,
    notes: "2003-vs-2004 boundary inconsistency in source",
  },
  {
    assetCode: "machinery-lifts-hydraulic",
    asset: "Lifts - hydraulic",
    tableSourceKey: "table_4_machinery",
    category: "Machinery (lifts/escalators)",
    elPre2004: 20,
    el2004to2019: 30,
    elPost2019: 30,
    capitalWorksOnly: false,
    isOwnEstimate: false,
    notes: "2003-vs-2004 boundary inconsistency in source",
  },
  {
    assetCode: "aircon-air-handling-units",
    asset: "Air handling units",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-chillers-absorption",
    asset: "Chillers - absorption",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 25,
    el2004to2019: 25,
    elPost2019: 25,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-chillers-centrifugal",
    asset: "Chillers - centrifugal",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-chillers-volumetrics-air-cooled-recip-rotary-screw-scroll",
    asset: "Chillers - volumetrics air-cooled (recip, rotary, screw, scroll)",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 15,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-chillers-volumetrics-water-cooled-recip-rotary-screw-scroll",
    asset: "Chillers - volumetrics water-cooled (recip, rotary, screw, scroll)",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-condensing-sets",
    asset: "Condensing sets",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 15,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-cooling-towers",
    asset: "Cooling towers",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 15,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-damper-motors-incl-vav-box-controller",
    asset: "Damper motors (incl VAV box controller)",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-fan-coil-units-connected-to-condensing-set",
    asset: "Fan coil units (connected to condensing set)",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 15,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-mini-split-systems-up-to-20kw-ceiling-floor-high-wall",
    asset: "Mini split systems up to 20kW (ceiling, floor, high wall)",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-packaged-air-conditioning-units",
    asset: "Packaged air conditioning units",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 15,
    el2004to2019: 15,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-pumps-hvac",
    asset: "Pumps (HVAC)",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 20,
    el2004to2019: 20,
    elPost2019: 20,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-room-units",
    asset: "Room units",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: 10,
    el2004to2019: 10,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-air-conditioning-ducts-pipes-vents",
    asset: "Air conditioning ducts, pipes, vents",
    tableSourceKey: "table_5_air_conditioning",
    category: "Air conditioning",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
  {
    assetCode: "aircon-pre2004-central-type-incl-ducting-vents",
    asset: "Central type (incl ducting, vents)",
    tableSourceKey: "table_air_conditioning_plant_pre2004",
    category: "Air conditioning (pre-2004 legacy)",
    elPre2004: 13.333,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: false,
    isOwnEstimate: false,
    notes: "Legacy table for assets acquired before 1 Jul 2004. Deprecated for new acquisitions.",
  },
  {
    assetCode: "aircon-pre2004-structural-alterations-additions-integral-to-the-plant",
    asset: "Structural alterations/additions integral to the plant",
    tableSourceKey: "table_air_conditioning_plant_pre2004",
    category: "Air conditioning (pre-2004 legacy)",
    elPre2004: 100,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: false,
    isOwnEstimate: false,
    notes: "Legacy table for assets acquired before 1 Jul 2004. Deprecated for new acquisitions.",
  },
  {
    assetCode: "aircon-pre2004-room-units",
    asset: "Room units",
    tableSourceKey: "table_air_conditioning_plant_pre2004",
    category: "Air conditioning (pre-2004 legacy)",
    elPre2004: 10,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: false,
    isOwnEstimate: false,
    notes: "Legacy table for assets acquired before 1 Jul 2004. Deprecated for new acquisitions.",
  },
  {
    assetCode: "aircon-pre2004-solar-energy-powered",
    asset: "Solar-energy powered",
    tableSourceKey: "table_air_conditioning_plant_pre2004",
    category: "Air conditioning (pre-2004 legacy)",
    elPre2004: 13.333,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: false,
    isOwnEstimate: false,
    notes: "Legacy table for assets acquired before 1 Jul 2004. Deprecated for new acquisitions.",
  },
  {
    assetCode: "evap-cooler-fixed-excl-ducting-vents",
    asset: "Fixed (excl ducting, vents)",
    tableSourceKey: "table_6_evaporative_coolers",
    category: "Evaporative coolers",
    elPre2004: null,
    el2004to2019: 20,
    elPost2019: 15,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "evap-cooler-portable",
    asset: "Portable",
    tableSourceKey: "table_6_evaporative_coolers",
    category: "Evaporative coolers",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: 10,
    capitalWorksOnly: false,
    isOwnEstimate: true,
  },
  {
    assetCode: "evap-cooler-ducting-and-vents",
    asset: "Ducting and vents",
    tableSourceKey: "table_6_evaporative_coolers",
    category: "Evaporative coolers",
    elPre2004: null,
    el2004to2019: null,
    elPost2019: null,
    capitalWorksOnly: true,
    isOwnEstimate: false,
  },
] as const;
